import CloudProviderIcon from 'components/IntegrationTypeIcon';
import SummaryCard from 'components/SummaryCard';
import React from 'react';
import { Badge } from 'reactstrap';

import './BPAFindingSummaryCard.css';

import {
  BPAFindingScanStatus,
  BPAFindingUserStatus,
} from '../../types/bpaFindings.types';

export interface BPAFindingSummaryData {
  id: number;
  description?: string | null;
  control_description?: string | null;
  integration_integration_label?: string | null;
  integration_module_code?: string | null;
  integration_provider_code?: string | null;
  region?: string | null;
  benchmark_name?: string | null;
  control_severity?: string | null;
  scan_status?: BPAFindingScanStatus | null;
  user_status?: BPAFindingUserStatus | null;
  status?: BPAFindingScanStatus | BPAFindingUserStatus | null;
  status_display?: string | null;
  user_status_display?: string | null;
  integration?: {
    integration_label?: string | null;
    module_code?: string | null;
    provider_code?: string | null;
  } | null;
  benchmark?: {
    description?: string | null;
  } | null;
  control?: {
    description?: string | null;
    severity?: string | null;
  } | null;
}

interface BPAFindingSummaryCardProps {
  rowData: BPAFindingSummaryData;
}

const statusMap: Record<
  BPAFindingScanStatus,
  { label: string; color: string }
> = {
  Active: { label: 'Active', color: 'danger' },
  Fixed: { label: 'Fixed', color: 'success' },
};

const SUPPORTED_PROVIDER_CODES = ['aws', 'gcp', 'azure', 'm365', 'other'];

const providerIconWrapperStyle: React.CSSProperties = {
  alignSelf: 'stretch',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '56px',
  minHeight: '72px',
};

const BPAFindingSummaryCard: React.FC<BPAFindingSummaryCardProps> = ({
  rowData,
}) => {
  const integration = rowData.integration || {};
  const benchmark = rowData.benchmark || {};
  const control = rowData.control || {};
  const scanStatus = rowData.scan_status;
  const isIgnored =
    rowData.status === 'Ignored' || rowData.user_status === 'Ignored';
  const status = scanStatus ? statusMap[scanStatus] : undefined;
  const statusDisplay = status || {
    label: scanStatus?.toUpperCase() || 'Unknown',
    color: 'secondary',
  };
  const userStatusLabel = rowData.user_status_display || 'Ignored';
  const providerCode = (
    rowData.integration_provider_code || integration.provider_code
  )?.toLowerCase();
  const providerIcon =
    providerCode && SUPPORTED_PROVIDER_CODES.includes(providerCode) ? (
      <CloudProviderIcon cloudProviderType={providerCode} size={48} />
    ) : (
      <i
        className="fa fa-plug text-primary"
        aria-hidden="true"
        title="Unknown provider"
        style={{ fontSize: '2.5rem' }}
      />
    );

  const summaryData = [
    {
      key: 'Integration',
      value:
        rowData.integration_integration_label ||
        integration.integration_label ||
        '-',
    },
    {
      key: 'Benchmark',
      value: rowData.benchmark_name || benchmark.description || '-',
    },
    {
      key: 'Module',
      value: rowData.integration_module_code || integration.module_code || '-',
    },
    { key: 'Region', value: rowData.region || '-' },
    {
      key: 'Severity',
      value: rowData.control_severity || control.severity || '-',
    },
  ];
  const title =
    (
      rowData.description ||
      rowData.control_description ||
      control.description ||
      ''
    ).trim() || `Finding ${rowData.id}`;

  return (
    <div className="bpa-finding-summary-card">
      <SummaryCard
        heading={
          <div className="d-flex align-items-center flex-wrap">
            <span style={{ fontWeight: 600 }}>
              {title}
              <Badge color={statusDisplay.color} pill className="ml-2">
                {statusDisplay.label}
              </Badge>
              {isIgnored && (
                <Badge color="warning" pill className="ml-1">
                  {userStatusLabel}
                </Badge>
              )}
            </span>
          </div>
        }
        data={summaryData}
        icon={<div style={providerIconWrapperStyle}>{providerIcon}</div>}
        mode="normal"
      />
    </div>
  );
};

export default BPAFindingSummaryCard;
